/**
 * DO NOT CALL THIS CLASS DIRECTLY.  Use scoreboard/app-display, e.g.
 * appDisplay.dialog(view)
 *
 * Usage:
 *
 * <pre>
 * var view = new Backbone.View()
 * appDisplay.dialog(view)
 * </pre>
 *
 * dialog.js handles the showing of views inside our dialogs as well as the sizing
 * and building of the dialogs themselves.
 */

import $ from 'jquery'

import Backbone from 'backbone'
import template from './dialog.hbs'
import _ from 'lodash'
import SizeSpy from 'scoreboard/components/size-spy'
import deviceType from 'scoreboard/components/device-type'
import logPath from 'scoreboard/components/log-path'
import autofocus from 'scoreboard/polyfills/autofocus'
import mobile from 'scoreboard/components/mobile'

/**
 * Should all non-dialog inputs be disabled when the dialog is rendered
 * @type {boolean}
 */
var disableInputs = deviceType.isMobile()

/**
 * Returns the maximum allowable height of the .dialog-body given the amount of
 * space available in teh viewPort
 * @param {Object} el the dialog dom element in question
 * @return {Number}  The max allowable height of the .dialog-body in pixels
 */
var getViewPortAllowance = function(el) {
	// ticket:9588, measurements are sometimes factional because (I think) of
	// the css scale transforms being used in some components. Fractional
	// measurements can cause the dialog box to jump around a little as we
	// resize it to fit the screen and grow for new content. This shows up
	// most when opening a new select drop down which ends up vertically
	// misaligned with the box.
	// Rounding the measurements to the nearest whole tends to give a more
	// consistent experience.
	var head = Math.round($(el).find('.dialog-header').outerHeight())
	var foot = Math.round($(el).find('.dialog-footer').outerHeight())

	var topAndBottom = head + foot
	let safariBottomBarHeight = deviceType.iPhone ? window.screen.height - window.screen.availHeight : 0
	return ($(window).height() - topAndBottom - safariBottomBarHeight) * .92
}

var Dialog = Backbone.View.extend({
	className: 'dialog',

	events: {
		// Most dialogs will have a cancel/close button in their content. When
		// those closing buttons are clicked, trigger the close event here in the dialog.
		// Note: A view can have it's own `click .close/.cancel` event listener which will
		// fire before this one does.
		'click .close, .cancel': 'close'
	},

	/**
	 * @param {String|Element|jQuery|Array} options.content The content which is to be shown in this dialog
	 */
	initialize: function(options) {
		this.options = options || {}
		this._content = this.options.content
		_.bindAll(this, 'resize', 'close')

		// handle 'render' events by re-rendering the content and maintaining the
		// scroll distance
		if (_.get(this._content, 'render')) {
			this._content.on('render', function() {
				// maintain where the user was scrolled
				// not a huge fan of reaching outside of this view but I can't
				// think of an alternative right now
				var scrollTop = this.$('.dialog-body').scrollTop()
				this._content.render()
				this.resize()
				this.$('.dialog-body').scrollTop(scrollTop)
			}.bind(this))
		}
		Backbone.history.on('route', this.routeListener = () => this.close())
	},

	render: function() {
		this.$el.html(template())

		// must be before the call to this.content
		this.sizeSpy = new SizeSpy({
			el: this.$('.the-dialog'),
			classes: [ 'dialog-small', 'dialog-medium', 'dialog-large', 'dialog-x-large' ]
		})

		if (this._content) {
			this.content(this._content)
		}

		// Now that everything's rendered, the 'open' class will make it visible
		this.$el.addClass('open')

		this.resize()

		this.$el.one('transitionend', function() {
			this.isDoneOpeningTransition = true
			this._measureFooterButtons()
			this.resize()
			if (this._content && this._content.trigger) {
				this._content.trigger('opened')
			}
		}.bind(this))

		this._disableInputs()
		this._attachListeners()
		Backbone.trigger('dialog:open')
		this.trigger('open')
		return this
	},

	content: function (content) {
		// might not have content if someone did something like
		// appDisplay.display(xhr, function() { appDisplay.display() })
		// that's not really the ideal syntax, but it's the best I could come
		// with to support wizards (e.g. datasets/filter-wizard)
		if (!content) {
			return
		}

		this._removeContent()
		this._content = content

		if (this._content.render) {
			this.$('.dialog-background').empty().append(this._content.el)
			this._content.render()
		} else {
			this.$('.dialog-background').empty().append(this._content)
		}

		// Now we can size it appropriately
		this.resize()

		// throttle the dialog resizes or all versions of ie go absolutely nuts
		var dialogHeader = this.$('.dialog-header')
		if (dialogHeader.length > 0) { // loading dots for the scorecard info don't have dialog-body-content
			this.resizeObserver = new MutationObserver(_.throttle(this.resize, 300))
			this.resizeObserver.observe(dialogHeader[0], {
				childList: true,
				subtree: true,
				attributes: true // listen when styles change, e.g. visibility changes
			})
		}

		logPath('dialog', this._content)
		autofocus(this.el)

		if (this._content.on) {
			this._content.on('resize', this.resize.bind(this))
			if (this._content.once) {
				this._content.once('done cancel close', () => this.close())
			}
		}
	},

	_attachListeners: function() {
		// Take note of when an input is focused upon inside a dialog. This is
		// used to lock scroll positioning inside various dialogs to make things
		// easier for mobile browsers and their keyboards (if they're being used)
		if (deviceType.safari && deviceType.mobileIOSAndriod) {
			this.$el.on('focus', 'input, textarea', function() {
				this.$el.addClass('dialog-input-focus')
			}.bind(this))
			this.$el.on('blur', 'input, textarea', function() {
				this.$el.removeClass('dialog-input-focus')
			}.bind(this))
		}

		$(window).on('resize', this.resize)
	},

	resize: function (inputFocusResize) {
		var safariInset = 0
		var safariBottomBarHeight = 0
		var dialogBody = this.$('.dialog-body')
		if (deviceType.iPhone) {
			safariBottomBarHeight = window.screen.height - window.screen.availHeight
			safariInset = window.screen.availHeight - window.innerHeight
		}
		// the iPad setup is a little different. In the way we layout the screen
		// now, there's no need to adjust it.

		// figure out where we are scrolled to right now. This will be important
		// if we resize the box so that we can return to our current position.
		var formerScrollTop = dialogBody.scrollTop()

		// Only adjust buttons if the dialog is open; the button widths are more accurate
		// that when the dialog is visible
		if (this.$el.hasClass('open')) {
			this._footerButtonsOverflow()
		}

		// If maxDimensions = true, have the dialog take up the entire screen minus
		// margins on the outside edges. If false/undefined, the dialog will only
		// take up the space needed with a max set by getViewPortAllowance()
		// If fillHeight = true, fill up the entire screens height, but allow the
		// width to to work as it normally does.
		if (this._content.maxDimensions) {
			this.$('.the-dialog').width('100%')
		}
		let heightProperty = this._content.maxDimensions || this._content.fillHeight ?
			'height' : 'max-height'
		let allowance = getViewPortAllowance(this.$el)
		dialogBody.css(heightProperty, allowance)
		dialogBody.scrollTop(formerScrollTop)
		this.sizeSpy.spy()
		// call resize on the content of the dialog in case anything (trees)
		// need to be adjusted on resize
		if (this._content) {
			this._content.resize && this._content.resize(allowance)
		}
		if (deviceType.safari && deviceType.mobileIOSAndriod) {
			this.$('.vertical-spacer').css({
				'font-size': 'calc(100vh - ' + (safariInset - safariBottomBarHeight) + 'px)'
			})

			// Prevent iOS Safari bug where elements beneath the modal overlay are still scrollable.
			// If the touch event is on the .dialog-holder, that means touch even was on the dark overlay,
			// and we can stop the scrolling.
			document.ontouchmove = function(e) {
				if ($(e.target).is('.dialog-holder')) {
					e.preventDefault()
				}
			}.bind(this)
		}
		this.$('.the-dialog').css('max-height', 'none')

		// Trigger a custom 'scroll' event which will close any open selects in the dialog.
		// This prevents them from sticking around when the dialog is programmatically
		// resized since the scoreboard-selects listen for this scroll event to close their menus #32007
		this.el.dispatchEvent(new Event('scroll'))
	},

	// Measure the widths of our buttons so we can use them later in calculating
	// footer overflow. Must be done before resize() so that they assume their
	// full width (before .button-overflow is applied).
	_measureFooterButtons: function() {
		this.$('.dialog-footer button').toggleClass('is-not-visible', true)
		for (var i = 0; i < this.$('.dialog-footer button').length; i++) {
			var button = this.$('.dialog-footer button')[i]
			// Make sure we're getting an accurate natural-width reading
			$(button).css({
				'width': 'auto',
				'max-width': 'none',
				'min-width': '0px'
			})
			this.footerButtonsWidth += $(button).outerWidth() + 10 // 10px margin
			$(button).removeAttr('style')
		}
		this.$('.dialog-footer button').toggleClass('is-not-visible', false)
	},

	// If the buttons in the footer wont fit or if the viewport width < mobile width
	// and there are > 2 buttons present, append .button-overflow
	// to envoke responsive styling
	_footerButtonsOverflow: function() {
		var footer = this.$('.dialog-footer')
		var isOverflow = footer.innerWidth() <= this.footerButtonsWidth ||
						footer.find('button').length > 2 && mobile.isMobileWidth()
		this.$('.dialog-footer').toggleClass('button-overflow', isOverflow)
	},

	escape: function() {
		// Call 'close' on _content (if it exists) so that it can handle anything it needs to
		// do and then trigger it's own 'close' event when it's done. That last close event
		// will be picked up here on the dialog level, and the dialog will be closed accordingly.
		// If there's no close method on the _content, just call close here at the
		// dialog level.
		if (this._content.close) {
			this._content.close()
		} else {
			this.close()
		}
	},

	/**
	 * Removes the markup for this dialog and moves the darkening mask or removes
	 * it, depending on whether there are other dialogs open.
	 * May be called when a view within a dialog triggers a 'close', 'cancel', or 'done' event
	 * or when the ESC key is hit, and the dialog-stack calls the escape() function.
	 * See comments in dialog-stack.js for complete explanation.
	 */
	close: function() {
		this._enableInputs()
		this.$el.removeClass('open')
		// #8726 immediately stop listening for events in the view
		_.result(this._content, 'stopListening')

		// #12905 We don't want marked-fields displaying errors
		this.removeMarkedTooltips()

		let completeClose = () => {
			this.remove()
			this.trigger('close')
		}

		// #32545 if the dialog is done with its opening scale transition, wait
		// until the closing transition finishes to completely close the dialog.
		// Else, close it right away.
		if (this.isDoneOpeningTransition) {
			this.$el.on('transitionend', () => completeClose())
		} else {
			completeClose()
		}
	},

	/**
	 * Returns whether the dialog is currently open.  When a dialog is closed,
	 * it loses the open status for a moment, then an animation plays, and then
	 * the dialog is removed.
	 */
	isOpen() {
		return this.$el.hasClass('open')
	},

	/**
	 * Removes marked field tooltips and prevents them from being toggled
	 * if the dialog isn't the highest in the stack.
	 */
	removeMarkedTooltips: function() {
		_.forEach(this.$('.marked-field'), el => {
			let markedField = $(el).data('view')
			if (markedField) {
				markedField.removeTooltip()
				markedField.tooltipHidden = true
			}
		})
	},

	_removeContent: function () {
		if (this._content && this._content.remove && $.contains(document, this._content.el)) {
			this._content.remove()
			delete this._content
		}
	},

	remove: function() {
		Backbone.history.off('route', this.routeListener)
		this.resizeObserver && this.resizeObserver.disconnect()
		$(window).off('resize', this.resize)
		this._removeContent()
		this.trigger('close')
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.sizeSpy], 'remove')
	},

	/**
	 * #11941 - disable all form elements behind the dialog because on mobile
	 * keyboards the arrows to navigate between form elements will be enabled.
	 *
	 * Finds all enabled inputs not in the dialog and disables them.  They are marked
	 * with a new class so they can easily be enabled later
	 *
	 * @private
	 */
	_disableInputs: function() {
		// Unfocus the button that was clicked to open the dialog.
		// This prevents the user from being able to open duplicate dialogs if they
		// hit enter/spacebar immediately after the dialog is opened.
		$('button:focus').blur()

		if (disableInputs) {
			var inputs = ['select', 'input', 'textarea', 'button']
			var selector = _.join(_.flatMap(inputs, function(i) {
				return i + ':not(:disabled)'
			}), ',')
			var inputs = $(selector)
				.filter(function(i, input) {
					return this.$el.has(input).length === 0 && !input.classList.contains('mobile-hidden-input')
				}.bind(this))
				.addClass('dialog-disabled')
				.prop('disabled', true)
		}
	},

	_enableInputs: function() {
		disableInputs && $('.dialog-disabled').prop('disabled', false)
	}
})

/**
 * Return true if the given DOM element is contained within a dialog
 * @param {HTMLElement} el
 */
function isInDialog(el) {
	return $(el).parents('.dialog').length > 0 || $(el).parents('.over-dialog').length > 0
}

export default Dialog
export { getViewPortAllowance }
export { isInDialog }
Dialog.__filename__ = 'components/dialog.js'
